<template>
    <div class="merge-review-page">
        <header class="merge-review-header">
            <div>
                <b-button variant="link" size="sm" :to="{name: 'employees'}">
                    <i class="fas fa-arrow-left mr-1"></i>К сотрудникам
                </b-button>
                <h1>Сверка и объединение сотрудников</h1>
                <p>
                    Найдите несколько карточек одного человека или разберите кандидатов после импорта.
                    Выберите основную карточку и сравните данные перед объединением.
                </p>
            </div>
            <b-button
                v-if="permissions.can_review"
                variant="outline-primary"
                :disabled="refreshing"
                @click="refreshCases"
            >
                <b-spinner v-if="refreshing" small class="mr-1"></b-spinner>
                <i v-else class="fas fa-sync-alt mr-1"></i>
                Обновить кандидатов
            </b-button>
        </header>
        <employee-merge-workbench v-if="workbenchVisible" :initial-employees="workbenchEmployees"
            :initial-survivor="workbenchSurvivor" :can-merge="permissions.can_merge"
            @closed="workbenchVisible = false" @merged="groupMerged" />

        <b-alert v-if="successMessage" show dismissible variant="success">
            {{ successMessage }}
        </b-alert>
        <b-alert v-if="errorMessage" show variant="danger">
            {{ errorMessage }}
        </b-alert>

        <div v-if="permissions.can_merge" class="merge-review-mode mb-3" role="group" aria-label="Режим сверки">
            <b-button :variant="reviewMode === 'queue' ? 'primary' : 'outline-primary'" :pressed="reviewMode === 'queue'"
                :disabled="bulkMerging" @click="reviewMode = 'queue'">Очередь сверки</b-button>
            <b-button :variant="reviewMode === 'manual' ? 'primary' : 'outline-primary'" :pressed="reviewMode === 'manual'"
                :disabled="bulkMerging" @click="selectManualMode">Выбрать вручную</b-button>
        </div>
        <section v-if="reviewMode === 'manual'" class="merge-review-manual">
            <h2>Объединить карточки одного человека</h2>
            <p>Найдите от 2 до 10 карточек по ФИО или ID, даже если их нет в очереди сверки.
                Затем выберите основную карточку, сравните данные и проверьте объединение.</p>
            <b-button v-if="permissions.can_merge" variant="primary" @click="openGroup()">
                <i class="fas fa-user-plus mr-1" aria-hidden="true"></i>Выбрать карточки сотрудников
            </b-button>
        </section>
        <template v-else>
        <section class="merge-review-toolbar">
            <div class="merge-review-tabs">
                <button
                    v-for="item in statusOptions"
                    :key="item.value"
                    type="button"
                    :class="['merge-review-tab', {'is-active': statusFilter === item.value}]"
                    @click="statusFilter = item.value"
                >
                    {{ item.label }} <strong>{{ statusTotal(item.value) }}</strong>
                </button>
            </div>
            <div class="merge-review-toolbar__actions">
                <b-button
                    v-if="permissions.can_merge && readyCases.length"
                    variant="danger"
                    :disabled="bulkMerging"
                    @click="mergeAllReady"
                >
                    <b-spinner v-if="bulkMerging" small class="mr-1"></b-spinner>
                    <i v-else class="fas fa-layer-group mr-1"></i>
                    Объединить все с высокой уверенностью: {{ readyCases.length }}
                </b-button>
                <b-input-group class="merge-review-search">
                    <b-input-group-prepend is-text><i class="fas fa-search"></i></b-input-group-prepend>
                    <b-form-input
                        v-model.trim="query"
                        type="search"
                        placeholder="ФИО, ID или табельный номер"
                    ></b-form-input>
                </b-input-group>
            </div>
        </section>

        <b-alert v-if="bulkMerging" show variant="info">
            Объединяю пары с высокой уверенностью: {{ bulkProgressCurrent }} из {{ bulkProgressTotal }}.
            Каждая пара повторно проверяется непосредственно перед объединением.
        </b-alert>

        <div v-if="loading" class="merge-review-state">
            <b-spinner class="mr-2"></b-spinner>Загружаю очередь сверки
        </div>

        <template v-else>
            <article
                v-for="mergeCase in groupedCases"
                :key="mergeCase.id"
                class="merge-review-case"
            >
                <div class="merge-review-case__head">
                    <div>
                        <b-badge :variant="caseBadgeVariant(mergeCase)">
                            {{ caseBadgeLabel(mergeCase) }}
                        </b-badge>
                        <span>Случай #{{ mergeCase.id }}</span>
                    </div>
                    <small v-if="hasStartDateEvidence(mergeCase)">
                        <i class="fas fa-calendar-check mr-1"></i>Совпала дата приёма
                    </small>
                </div>

                <div class="merge-review-comparison">
                    <div>
                        <section v-for="candidateCase in relatedCases(mergeCase)" :key="candidateCase.id" class="merge-review-person">
                            <small>Похожая карточка · #{{ candidateCase.source_employee.employee_id }}</small>
                            <router-link :to="employeeLink(candidateCase.source_employee)" target="_blank">
                                {{ employeeName(candidateCase.source_employee) }} ↗
                            </router-link>
                            <dl>
                                <div><dt>Дата рождения</dt><dd>{{ displayBirth(candidateCase.source_employee) }}</dd></div>
                                <div><dt>Статус</dt><dd>{{ displayLifecycle(candidateCase.source_employee) }}</dd></div>
                                <div><dt>Должность</dt><dd>{{ candidateCase.source_employee.operational_position || '—' }}</dd></div>
                                <div><dt>Учётная запись</dt><dd>{{ accountLabel(candidateCase.source_employee) }}</dd></div>
                            </dl>
                            <div v-for="item in (candidateCase.evidence || {}).comparisons || []" :key="item.field" class="small"
                                :class="item.state === 'different' ? 'text-danger' : 'text-muted'">{{ item.text }}</div>
                            <b-button v-if="permissions.can_review" size="sm" variant="outline-danger" :disabled="bulkMerging"
                                @click="openReject(candidateCase)">Другой человек</b-button>
                        </section>
                    </div>

                    <i class="fas fa-long-arrow-alt-right merge-review-arrow"></i>

                    <section class="merge-review-person merge-review-person--survivor">
                        <small>{{ (mergeCase.evidence || {}).origin === '1c_import' ? 'Импортировано · ожидает сверки' : 'Предлагаемая основная карточка' }}</small>
                        <router-link :to="employeeLink(mergeCase.survivor_employee)" target="_blank">
                            {{ employeeName(mergeCase.survivor_employee) }}
                        </router-link>
                        <span>#{{ mergeCase.survivor_employee.employee_id }}</span>
                        <dl>
                            <div><dt>Дата рождения</dt><dd>{{ displayBirth(mergeCase.survivor_employee) }}</dd></div>
                            <div><dt>Статус</dt><dd>{{ displayLifecycle(mergeCase.survivor_employee) }}</dd></div>
                            <div><dt>Табельный номер</dt><dd>{{ personnelNumber(mergeCase.survivor_employee) }}</dd></div>
                            <div><dt>Дата приёма</dt><dd>{{ employmentStart(mergeCase.survivor_employee) }}</dd></div>
                            <div><dt>Должность</dt><dd>{{ mergeCase.survivor_employee.operational_position || '—' }}</dd></div>
                            <div><dt>Учётная запись</dt><dd>{{ accountLabel(mergeCase.survivor_employee) }}</dd></div>
                        </dl>
                    </section>
                </div>

                <b-alert v-if="hasTwoAccounts(mergeCase)" show variant="danger" class="mx-3 mb-3">
                    <i class="fas fa-user-lock mr-1"></i>
                    У обеих карточек есть учётные записи. Перед объединением нужно проверить их рабочие связи.
                </b-alert>

                <div class="merge-review-actions">
                    <b-button v-if="permissions.can_merge" size="sm" variant="outline-primary" :disabled="bulkMerging"
                        @click="openGroup(mergeCase)">Сравнить всю группу ({{ relatedEmployees(mergeCase).length }})</b-button>
                    <div v-if="mergeCase.evidence && mergeCase.evidence.comparisons" class="small text-muted mr-auto">
                        <div v-for="item in mergeCase.evidence.comparisons" :key="item.field"
                            :class="{'text-danger': item.state === 'different', 'text-warning': item.state === 'typo'}">{{ item.text }}</div>
                    </div>
                    <b-button
                        v-if="permissions.can_review"
                        size="sm"
                        variant="outline-danger"
                        :disabled="bulkMerging"
                        @click="openReject(mergeCase)"
                    >
                        Разные сотрудники (эта пара)
                    </b-button>
                    <b-button
                        size="sm"
                        variant="primary"
                        :disabled="bulkMerging"
                        @click="openGroup(mergeCase)"
                    >
                        Проверить объединение
                    </b-button>
                </div>
            </article>

            <div v-if="!filteredCases.length" class="merge-review-empty">
                <i class="fas fa-check-circle"></i>
                <strong>Подходящих случаев нет</strong>
                <span>Измените фильтр или обновите список кандидатов.</span>
            </div>
        </template>
        </template>

        <b-modal
            v-model="previewVisible"
            size="xl"
            title="Проверка объединения"
            centered
            scrollable
            hide-footer
            @hidden="clearPreview"
        >
            <div v-if="previewLoading" class="merge-review-state">
                <b-spinner class="mr-2"></b-spinner>Проверяю связанные данные
            </div>
            <template v-else-if="preview">
                <div class="merge-review-preview-names">
                    <strong>{{ preview.source_employee.display_name }}</strong>
                    <i class="fas fa-arrow-right"></i>
                    <strong>{{ preview.survivor_employee.display_name }}</strong>
                </div>
                <b-alert
                    v-for="item in preview.conflicts"
                    :key="item.code"
                    show
                    :variant="isResolvableConflict(item) ? 'warning' : 'danger'"
                >
                    <strong v-if="isResolvableConflict(item)">Конфликт можно разрешить.</strong>
                    <strong v-else>Объединение заблокировано.</strong>
                    {{ item.message }}
                </b-alert>
                <b-alert
                    v-for="item in preview.warnings"
                    :key="item.code"
                    show
                    variant="warning"
                >
                    {{ item.message }}
                </b-alert>

                <h3>Будет перенесено</h3>
                <div class="merge-review-impact">
                    <div v-for="item in impactRows" :key="item.key">
                        <span>{{ item.label }}</span><strong>{{ item.value }}</strong>
                    </div>
                </div>

                <section v-if="preview.user_impacts" class="merge-review-user-impact">
                    <h3>Связи двух учётных записей</h3>
                    <div>
                        <ul>
                            <li v-for="item in userBlockers(preview.user_impacts.source)" :key="'s-' + item.label">
                                Исходная учётка — {{ item.label }}: {{ item.count }}
                            </li>
                            <li v-for="item in userBlockers(preview.user_impacts.survivor)" :key="'t-' + item.label">
                                Учётка 1С — {{ item.label }}: {{ item.count }}
                            </li>
                        </ul>
                    </div>
                </section>

                <b-alert
                    v-if="canResolveUserConflict(preview)"
                    show
                    variant="info"
                >
                    Неактивная исходная учётная запись
                    #{{ preview.user_account_resolution.source_user.user_id }} будет удалена.
                    Рабочие связи будут перенесены на активную учётную запись 1С
                    #{{ preview.user_account_resolution.survivor_user.user_id }}.
                    Историческое авторство действий не переписывается.
                    <ul v-if="preview.user_account_resolution.transferable_references.length" class="mb-0 mt-2">
                        <li
                            v-for="reference in preview.user_account_resolution.transferable_references"
                            :key="reference.table + '.' + reference.column"
                        >
                            {{ reference.label }}: {{ reference.count }}
                        </li>
                    </ul>
                </b-alert>

                <b-form-checkbox
                    v-if="permissions.can_merge && canMergePreview(preview)"
                    v-model="mergeConfirmed"
                    class="merge-review-confirmation"
                >
                    <template v-if="canResolveUserConflict(preview)">
                        Я проверил обе карточки и учётные записи. Оставить учётную запись основной карточки и объединить данные.
                    </template>
                    <template v-else>
                        Я проверил обе карточки и подтверждаю выбор основной карточки.
                    </template>
                </b-form-checkbox>

                <div class="merge-review-modal-actions">
                    <b-button variant="outline-secondary" @click="previewVisible = false">
                        Закрыть
                    </b-button>
                    <b-button
                        v-if="permissions.can_merge"
                        variant="danger"
                        :disabled="!canMergePreview(preview) || !mergeConfirmed || merging"
                        @click="mergeSelectedCase"
                    >
                        <b-spinner v-if="merging" small class="mr-1"></b-spinner>
                        Объединить карточки
                    </b-button>
                    <span v-else class="text-muted">
                        Объединение доступно пользователям с правом редактирования сотрудников.
                    </span>
                </div>
            </template>
        </b-modal>

        <b-modal
            v-model="rejectVisible"
            title="Это разные сотрудники"
            centered
            hide-footer
            @hidden="clearReject"
        >
            <b-form-group label="Причина решения" label-for="merge-reject-reason">
                <AppTextarea
                    id="merge-reject-reason"
                    v-model="rejectReason"
                    maxlength="2000"
                    placeholder="Например: разные отчества или разные люди с одинаковыми инициалами"
                ></AppTextarea>
            </b-form-group>
            <div class="merge-review-modal-actions">
                <b-button variant="outline-secondary" @click="rejectVisible = false">Отмена</b-button>
                <b-button
                    variant="danger"
                    :disabled="!rejectReason.trim() || rejecting"
                    @click="rejectSelectedCase"
                >
                    <b-spinner v-if="rejecting" small class="mr-1"></b-spinner>
                    Подтвердить: разные сотрудники
                </b-button>
            </div>
        </b-modal>
    </div>
</template>

<script>
import api from '@/utils/api';
import EmployeeMergeWorkbench from './EmployeeMergeWorkbench.vue';

const IMPACT_LABELS = {
    contact_methods: 'Контакты',
    contact_addresses: 'Адреса',
    external_profiles: 'Внешние профили',
    employment_periods: 'Периоды занятости',
    skills: 'Навыки',
    characteristics: 'Характеристики',
    assignment_events: 'История распределения',
    object_assignments: 'Дополнительные объекты',
    planned_moves: 'Плановые перемещения',
    absences: 'Временные отсутствия',
    scheduled_terminations: 'Плановые увольнения',
    history_events: 'История карточки',
    timesheet_lines: 'Строки табелей',
    resolved_timesheet_people: 'Сопоставления людей в табелях'
};

export default {
    name: 'EmployeeMergeReview',
    components: {EmployeeMergeWorkbench},
    data() {
        return {
            cases: [],
            reviewMode: 'queue',
            workbenchVisible: false,
            workbenchEmployees: [],
            workbenchSurvivor: null,
            totals: {},
            permissions: {can_review: false, can_merge: false},
            statusFilter: '',
            query: '',
            loading: false,
            refreshing: false,
            errorMessage: '',
            successMessage: '',
            previewVisible: false,
            previewLoading: false,
            preview: null,
            mergeConfirmed: false,
            merging: false,
            bulkMerging: false,
            bulkProgressCurrent: 0,
            bulkProgressTotal: 0,
            rejectVisible: false,
            rejectCase: null,
            rejectReason: '',
            rejecting: false,
            statusOptions: [
                {value: '', label: 'Все открытые'},
                {value: 'ready', label: 'Высокая уверенность'},
                {value: 'suggested', label: 'Нужна сверка'},
                {value: 'blocked', label: 'Есть конфликт'}
            ]
        };
    },
    computed: {
        groupedCases() {
            const groups = new Map();
            this.filteredCases.forEach(item => {
                const id = item.survivor_employee.employee_id;
                if (!groups.has(id)) groups.set(id, item);
            });
            return Array.from(groups.values());
        },
        filteredCases() {
            const query = this.normalize(this.query);
            return this.cases.filter(mergeCase => {
                if (this.statusFilter && mergeCase.status !== this.statusFilter) return false;
                if (!query) return true;
                const source = mergeCase.source_employee || {};
                const survivor = mergeCase.survivor_employee || {};
                const text = this.normalize([
                    mergeCase.id, source.employee_id, source.display_name,
                    source.operational_position, survivor.employee_id,
                    survivor.display_name, survivor.operational_position,
                    this.personnelNumber(survivor)
                ].filter(Boolean).join(' '));
                return query.split(' ').every(word => text.includes(word));
            });
        },
        readyCases() {
            return this.cases.filter(mergeCase => (
                mergeCase.status === 'ready' && mergeCase.confidence === 'strong'
            ));
        },
        impactRows() {
            const impact = this.preview && this.preview.impact || {};
            return Object.keys(IMPACT_LABELS).map(key => ({
                key, label: IMPACT_LABELS[key], value: Number(impact[key] || 0)
            }));
        }
    },
    created() {
        this.loadCases();
    },
    methods: {
        displayBirth(person) { return person.birth_date ? String(person.birth_date).slice(0, 10).split('-').reverse().join('.') : 'Нет данных'; },
        displayLifecycle(person) { return {active: 'Активен', reserve: 'Резерв', unverified: 'Требует сверки'}[person.lifecycle_status] || 'Не указан'; },
        relatedCases(mergeCase) {
            return this.cases.filter(item => Number(item.survivor_employee.employee_id) === Number(mergeCase.survivor_employee.employee_id)
                && ['suggested', 'ready', 'blocked'].includes(item.status));
        },
        relatedEmployees(mergeCase) {
            const people = new Map();
            this.relatedCases(mergeCase).forEach(item => {
                people.set(item.survivor_employee.employee_id, item.survivor_employee);
                people.set(item.source_employee.employee_id, item.source_employee);
            });
            return Array.from(people.values());
        },
        openGroup(mergeCase) {
            if (!this.permissions.can_merge || this.bulkMerging) return;
            this.workbenchEmployees = mergeCase ? this.relatedEmployees(mergeCase) : [];
            this.workbenchSurvivor = mergeCase ? Number(mergeCase.survivor_employee.employee_id) : null;
            this.workbenchVisible = true;
        },
        selectManualMode() {
            if (!this.permissions.can_merge || this.bulkMerging) return;
            this.reviewMode = 'manual';
            this.openGroup();
        },
        async groupMerged(result) {
            this.successMessage = `Объединено карточек: ${result.merged_count + 1}. Основная карточка #${result.redirect_employee_id}.`;
            await this.loadCases();
        },
        async loadCases() {
            this.loading = true;
            this.errorMessage = '';
            try {
                const response = await api.get('/api/employees/merge-cases', {ignoreModal: true});
                const data = response && response.data || {};
                this.cases = Array.isArray(data.cases) ? data.cases : [];
                this.totals = data.totals || {};
                this.permissions = Object.assign({}, this.permissions, data.permissions || {});
            } catch (error) {
                this.errorMessage = this.errorText(error, 'Не удалось загрузить очередь сверки.');
            } finally {
                this.loading = false;
            }
        },
        async refreshCases() {
            this.refreshing = true;
            this.errorMessage = '';
            this.successMessage = '';
            try {
                const response = await api.post('/api/employees/merge-cases/refresh', {}, {ignoreModal: true});
                const data = response && response.data || {};
                this.successMessage = `Найдено пар: ${data.detected_pairs || 0}. Высокая уверенность: ${data.ready_employees || 0}. Нужна сверка: ${data.review_employees || 0}.`;
                await this.loadCases();
            } catch (error) {
                this.errorMessage = this.errorText(error, 'Не удалось обновить кандидатов.');
            } finally {
                this.refreshing = false;
            }
        },
        async openPreview(mergeCase) {
            this.preview = null;
            this.mergeConfirmed = false;
            this.previewVisible = true;
            this.previewLoading = true;
            this.errorMessage = '';
            try {
                const response = await api.get(`/api/employees/merge-cases/${encodeURIComponent(mergeCase.id)}/preview`, {ignoreModal: true});
                this.preview = response ? response.data : null;
            } catch (error) {
                this.previewVisible = false;
                this.errorMessage = this.errorText(error, 'Не удалось проверить объединение.');
            } finally {
                this.previewLoading = false;
            }
        },
        async mergeAllReady() {
            if (!this.permissions.can_merge || this.bulkMerging || !this.readyCases.length) return;
            const candidates = this.readyCases.slice();
            const confirmed = await this.$bvModal.msgBoxConfirm(
                `Будут последовательно перепроверены и объединены ${candidates.length} пар. Карточка 1С в каждой паре останется основной. Продолжить?`,
                {
                    title: 'Объединить все пары с высокой уверенностью',
                    okTitle: `Объединить ${candidates.length}`,
                    cancelTitle: 'Отмена',
                    okVariant: 'danger',
                    cancelVariant: 'outline-secondary',
                    centered: true
                }
            );
            if (confirmed !== true) return;
            this.bulkMerging = true;
            this.bulkProgressCurrent = 0;
            this.bulkProgressTotal = candidates.length;
            this.errorMessage = '';
            this.successMessage = '';
            let merged = 0;
            let skipped = 0;
            const failures = [];
            try {
                for (const mergeCase of candidates) {
                    this.bulkProgressCurrent += 1;
                    try {
                        const previewResponse = await api.get(
                            `/api/employees/merge-cases/${encodeURIComponent(mergeCase.id)}/preview`,
                            {ignoreModal: true}
                        );
                        const preview = previewResponse && previewResponse.data;
                        if (!preview || !preview.can_merge) {
                            skipped += 1;
                            continue;
                        }
                        await api.post(
                            `/api/employees/merge-cases/${encodeURIComponent(mergeCase.id)}/merge`,
                            {state_token: preview.state_token},
                            {ignoreModal: true}
                        );
                        merged += 1;
                    } catch (error) {
                        failures.push({
                            caseId: mergeCase.id,
                            message: this.errorText(error, 'Ошибка объединения')
                        });
                    }
                }
                await this.loadCases();
                this.successMessage = `Объединено пар: ${merged} из ${candidates.length}.`;
                if (skipped || failures.length) {
                    const failedCases = failures.slice(0, 10)
                        .map(item => `#${item.caseId}: ${item.message}`).join('; ');
                    this.errorMessage = [
                        skipped ? `Пропущено после повторной проверки: ${skipped}.` : '',
                        failures.length ? `Ошибок: ${failures.length}. ${failedCases}` : ''
                    ].filter(Boolean).join(' ');
                }
            } catch (error) {
                this.errorMessage = this.errorText(error, 'Не удалось выполнить массовое объединение.');
            } finally {
                this.bulkMerging = false;
                this.bulkProgressCurrent = 0;
                this.bulkProgressTotal = 0;
            }
        },
        clearPreview() {
            if (!this.merging) {
                this.preview = null;
                this.mergeConfirmed = false;
            }
        },
        async mergeSelectedCase() {
            if (
                !this.preview
                || !this.canMergePreview(this.preview)
                || !this.mergeConfirmed
                || this.merging
            ) return;
            this.merging = true;
            this.errorMessage = '';
            try {
                const response = await api.post(
                    `/api/employees/merge-cases/${encodeURIComponent(this.preview.case_id)}/merge`,
                    {
                        state_token: this.preview.state_token,
                        resolve_user_conflict: this.canResolveUserConflict(this.preview)
                            ? 'keep_survivor'
                            : null
                    },
                    {ignoreModal: true}
                );
                const data = response && response.data || {};
                this.previewVisible = false;
                this.successMessage = `Карточки объединены. Основная карточка — #${data.redirect_employee_id}.`;
                await this.loadCases();
            } catch (error) {
                const data = error && error.response && error.response.data;
                if (data && data.details && data.details.preview) {
                    this.preview = data.details.preview;
                    this.mergeConfirmed = false;
                }
                this.errorMessage = this.errorText(error, 'Не удалось объединить карточки.');
            } finally {
                this.merging = false;
            }
        },
        openReject(mergeCase) {
            this.rejectCase = mergeCase;
            this.rejectReason = '';
            this.rejectVisible = true;
        },
        clearReject() {
            if (!this.rejecting) {
                this.rejectCase = null;
                this.rejectReason = '';
            }
        },
        async rejectSelectedCase() {
            if (!this.rejectCase || !this.rejectReason.trim() || this.rejecting) return;
            this.rejecting = true;
            this.errorMessage = '';
            try {
                await api.post(
                    `/api/employees/merge-cases/${encodeURIComponent(this.rejectCase.id)}/reject`,
                    {reason: this.rejectReason},
                    {ignoreModal: true}
                );
                this.rejectVisible = false;
                this.successMessage = 'Пара отмечена как разные сотрудники.';
                await this.loadCases();
            } catch (error) {
                this.errorMessage = this.errorText(error, 'Не удалось сохранить решение.');
            } finally {
                this.rejecting = false;
            }
        },
        employeeName(employee) {
            return employee && employee.display_name || 'Карточка недоступна';
        },
        employeeLink(employee) {
            return employee && employee.employee_id
                ? {name: 'employee-details', params: {id: String(employee.employee_id)}}
                : {name: 'employees'};
        },
        accountLabel(employee) {
            return employee && employee.user_id ? `#${employee.user_id}` : 'Нет';
        },
        personnelNumber(employee) {
            return employee && employee.latest_employment_period
                && employee.latest_employment_period.personnel_number || '—';
        },
        employmentStart(employee) {
            const value = employee && employee.latest_employment_period
                && employee.latest_employment_period.started_on;
            const match = String(value || '').slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
            return match ? `${match[3]}.${match[2]}.${match[1]}` : '—';
        },
        statusTotal(status) {
            if (!status) return Number(this.totals.ready || 0)
                + Number(this.totals.suggested || 0)
                + Number(this.totals.blocked || 0);
            return Number(this.totals[status] || 0);
        },
        caseBadgeVariant(mergeCase) {
            if (mergeCase.status === 'blocked') return 'danger';
            return mergeCase.confidence === 'strong' ? 'success' : 'warning';
        },
        caseBadgeLabel(mergeCase) {
            if (mergeCase.status === 'blocked') return 'Есть конфликт';
            return mergeCase.confidence === 'strong' ? 'Высокая уверенность' : 'Нужна сверка';
        },
        hasStartDateEvidence(mergeCase) {
            const dates = mergeCase.evidence && mergeCase.evidence.matching_start_dates;
            return Array.isArray(dates) && dates.length > 0;
        },
        hasTwoAccounts(mergeCase) {
            return Boolean(mergeCase.source_employee.user_id && mergeCase.survivor_employee.user_id);
        },
        canResolveUserConflict(preview) {
            return Boolean(
                preview
                && this.permissions.can_resolve_accounts
                && preview.user_account_resolution
                && preview.user_account_resolution.can_resolve
                && Array.isArray(preview.conflicts)
                && preview.conflicts.length === 1
                && preview.conflicts[0].code === 'TWO_USER_ACCOUNTS'
            );
        },
        canMergePreview(preview) {
            return Boolean(preview && (preview.can_merge || this.canResolveUserConflict(preview)));
        },
        isResolvableConflict(conflict) {
            return Boolean(
                conflict
                && conflict.code === 'TWO_USER_ACCOUNTS'
                && this.canResolveUserConflict(this.preview)
            );
        },
        userBlockers(impact) {
            return impact && Array.isArray(impact.blockers) && impact.blockers.length
                ? impact.blockers
                : [{label: 'Активных деловых связей не найдено', count: 0}];
        },
        normalize(value) {
            return String(value || '').toLocaleLowerCase('ru-RU')
                .replace(/ё/g, 'е').replace(/[^\p{L}\p{N}@+]+/gu, ' ')
                .replace(/\s+/g, ' ').trim();
        },
        errorText(error, fallback) {
            const data = error && error.response ? error.response.data : null;
            if (data && typeof data === 'object') return data.msg || data.message || fallback;
            return typeof data === 'string' && data ? data : fallback;
        }
    }
};
</script>

<style scoped>
.merge-review-mode {display:flex;flex-wrap:wrap;gap:8px}
.merge-review-manual {padding:20px;border:1px solid #dce5ee;border-radius:10px;background:#fff}
.merge-review-manual h2 {font-size:1.2rem}
.merge-review-manual p {max-width:780px;color:#6b7c8f}
.merge-review-page {min-height:100%;padding:16px 20px 28px;background:#f4f7fa;color:#26384c}
.merge-review-header,.merge-review-toolbar,.merge-review-case__head,.merge-review-actions,.merge-review-modal-actions{display:flex;align-items:center;justify-content:space-between;gap:12px}
.merge-review-header{align-items:flex-start;margin-bottom:14px}.merge-review-header h1{margin:2px 0 4px;font-size:1.55rem}.merge-review-header p{max-width:880px;margin:0;color:#6b7c8f}
.merge-review-toolbar{margin-bottom:12px;padding:10px;border:1px solid #dce5ee;border-radius:10px;background:#fff}.merge-review-tabs{display:flex;flex-wrap:wrap;gap:7px}.merge-review-tab{padding:6px 9px;border:1px solid #ccd8e4;border-radius:8px;background:#fff;color:#51677c}.merge-review-tab strong{margin-left:6px;padding:1px 6px;border-radius:999px;background:#edf2f7}.merge-review-tab.is-active{border-color:#2478dd;background:#eaf3ff;color:#1455a8}.merge-review-toolbar__actions{display:flex;flex:1;align-items:center;justify-content:flex-end;gap:8px}.merge-review-search{max-width:420px}
.merge-review-case{margin-bottom:12px;overflow:hidden;border:1px solid #d6e1eb;border-radius:12px;background:#fff;box-shadow:0 3px 10px rgba(40,67,91,.06)}.merge-review-case__head,.merge-review-actions{padding:10px 14px}.merge-review-case__head{border-bottom:1px solid #e5ebf1;background:#f9fbfd}.merge-review-case__head span{margin-left:8px;color:#8090a0;font-size:.84rem}
.merge-review-comparison{display:grid;grid-template-columns:minmax(0,1fr) 44px minmax(0,1fr);align-items:center;gap:12px;padding:14px}.merge-review-person{min-width:0;padding:13px;border:1px solid #dbe5ef;border-radius:10px;background:#f8fafc}.merge-review-person--survivor{border-color:#a9d4bd;background:#f2fbf5}.merge-review-person>small{display:block;color:#74879a;text-transform:uppercase}.merge-review-person>a{color:#173f70;font-size:1.05rem;font-weight:700}.merge-review-person>span{margin-left:7px;color:#8291a0}.merge-review-person dl{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:8px 16px;margin:12px 0 0}.merge-review-person dt{color:#8190a0;font-size:.76rem;font-weight:500}.merge-review-person dd{margin:1px 0 0;overflow-wrap:anywhere}.merge-review-arrow{color:#3e9365;font-size:1.45rem;text-align:center}.merge-review-actions{justify-content:flex-end;border-top:1px solid #e7edf3}
.merge-review-state,.merge-review-empty{display:flex;align-items:center;justify-content:center;min-height:180px;color:#718396}.merge-review-empty{flex-direction:column;gap:6px;border:1px dashed #c8d5e2;border-radius:12px;background:#fff}.merge-review-empty i{color:#43a36e;font-size:2rem}.merge-review-preview-names{display:flex;align-items:center;justify-content:center;gap:14px;margin-bottom:14px;font-size:1.08rem}.merge-review-impact{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:8px;margin-bottom:18px}.merge-review-impact>div{display:flex;justify-content:space-between;gap:8px;padding:9px 11px;border-radius:8px;background:#f2f6fa}.merge-review-user-impact{margin-bottom:18px;padding:12px;border:1px solid #f0c3c7;border-radius:10px;background:#fff7f7}.merge-review-modal-actions{justify-content:flex-end;margin-top:16px}
.merge-review-confirmation{margin-top:14px;padding:11px 12px 11px 34px;border:1px solid #d8e2eb;border-radius:8px;background:#f7f9fb}
@media(max-width:800px){.merge-review-page{padding:10px}.merge-review-header,.merge-review-toolbar,.merge-review-toolbar__actions{align-items:stretch;flex-direction:column}.merge-review-search{max-width:none}.merge-review-comparison{grid-template-columns:minmax(0,1fr)}.merge-review-arrow{transform:rotate(90deg)}.merge-review-impact{grid-template-columns:minmax(0,1fr)}}
</style>
